import { useEffect, useState, useMemo, useCallback } from "react";
import { Box } from "@mui/material";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import "../../assets/css/summary.css";
import { apiGet } from "../../services/genesisApi/httpsMethods";
import { useErrorDialog } from "../../hooks/useErrorDialog";
import { ErrorDialog } from "../../components/common/errorDialog";
import {
  condenseSectorSummaries,
  condenseStatusSummaries,
} from "./pipelineSummaryCondensed";
import { PipelineSummaryDonutChart } from "./summary/PipelineSummaryDonutChart";
import { PipelineSummaryTable } from "./summary/PipelineSummaryTable";
import { SUMMARY_PANEL_HEIGHT } from "./summary/summaryPanelConstants";

interface PipelineSummariesProps {
  groupBy: "status" | "sector" | "country" | "kill_reason";
  statusIds?: number[];
  condensed?: boolean;
}

const PANEL_HEADER_HEIGHT = 72;

function getGroupTitle(groupBy: PipelineSummariesProps["groupBy"]): string {
  if (groupBy === "status") return "Status";
  if (groupBy === "sector") return "Sector";
  if (groupBy === "country") return "Country";
  return "Kill Reason";
}

const PipelineSummaries = ({
  groupBy,
  statusIds = [],
  condensed = false,
}: PipelineSummariesProps) => {
  const [summary, setSummary] = useState<any>(null);
  const { errorDialog, showErrorFromResponse, hideError } = useErrorDialog();

  const statusIdsKey = useMemo(() => {
    return statusIds.slice().sort((a, b) => a - b).join(",");
  }, [statusIds.join(",")]);

  useEffect(() => {
    let url = `deals/pipeline/summary?group_by=${groupBy}`;
    if (statusIds.length > 0) {
      const statusIdsParam = statusIds.map((id) => `status_ids=${id}`).join("&");
      url += `&${statusIdsParam}`;
    }

    let cancelled = false;
    apiGet(url)
      .then((response) => {
        if (cancelled) return;
        if (response.success && response.data) {
          setSummary(response.data);
        } else {
          showErrorFromResponse(response);
        }
      })
      .catch(() => {
        if (cancelled) return;
        showErrorFromResponse({
          success: false,
          status: 500,
          message: `Failed to fetch ${groupBy} summary. Please try again.`,
        });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy, statusIdsKey]);

  const formatCurrency = (value: number) => {
    return value.toLocaleString("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const displaySummary = useMemo(() => {
    if (!summary?.summaries) return summary;

    if (!condensed) return summary;

    const condensedSummaries =
      groupBy === "status"
        ? condenseStatusSummaries(summary.summaries)
        : groupBy === "sector"
          ? condenseSectorSummaries(summary.summaries)
          : summary.summaries;

    return { ...summary, summaries: condensedSummaries };
  }, [summary, condensed, groupBy]);

  const maxLoanAmount = displaySummary?.total?.loan_amount_gbp
    ? displaySummary.total?.loan_amount_gbp
    : 0;

  const chartCellRenderer = useCallback((params: ICellRendererParams) => {
    const { percentage, percentOfTotal, name } = params.data;

    if (name === "Total") {
      return <span className="summary-percent-total">100%</span>;
    }

    return (
      <div className="summary-percent-cell">
        <div
          className={
            percentage > 0
              ? "summary-percent-cell__bar"
              : "summary-percent-cell__bar summary-percent-cell__bar--empty"
          }
          style={{ width: `${percentage}%` }}
          title={params.data.tooltip}
        />
        <span className="summary-percent-cell__value">
          {Math.round(percentOfTotal)}%
        </span>
      </div>
    );
  }, []);

  const summaryRowData = useMemo(() => {
    if (!displaySummary?.summaries) return [];

    const rows = displaySummary.summaries.map((item: any) => {
      const percentage =
        maxLoanAmount > 0 ? (item.loan_amount_gbp / maxLoanAmount) * 100 : 0;
      const percentOfTotal =
        displaySummary.total?.loan_amount_gbp &&
        displaySummary.total.loan_amount_gbp > 0
          ? (item.loan_amount_gbp / displaySummary.total.loan_amount_gbp) * 100
          : 0;

      return {
        name: item.name,
        deal_count: item.deal_count,
        loan_amount_gbp: item.loan_amount_gbp,
        average_deal_size_gbp: item.average_deal_size_gbp || 0,
        percentage,
        percentOfTotal,
        tooltip: `${item.name}: ${formatCurrency(item.loan_amount_gbp)}`,
      };
    });

    if (
      groupBy === "sector" ||
      groupBy === "country" ||
      groupBy === "kill_reason"
    ) {
      rows.sort((a: any, b: any) => b.loan_amount_gbp - a.loan_amount_gbp);
    }

    if (displaySummary.total) {
      rows.push({
        name: "Total",
        deal_count: displaySummary.total.deal_count,
        loan_amount_gbp: displaySummary.total.loan_amount_gbp,
        average_deal_size_gbp: displaySummary.total.average_deal_size_gbp || 0,
        percentage: 0,
        percentOfTotal: 100,
        tooltip: "",
      });
    }

    return rows;
  }, [displaySummary, maxLoanAmount, groupBy]);

  const summaryColumnDefs: ColDef[] = useMemo(() => {
    const getHeaderName = () => getGroupTitle(groupBy);

    return [
      {
        field: "name",
        headerName: getHeaderName(),
        width: 150,
        cellStyle: (params: any) => {
          if (params.data?.name === "Total") {
            return {
              backgroundColor: "#e8eaed",
              color: "#182737",
              borderTop: "3px solid #182737",
            };
          }
          return undefined;
        },
      },
      {
        field: "deal_count",
        headerName: "Deals",
        width: 80,
        cellStyle: (params: any) => {
          const baseStyle = { textAlign: "right" as const };
          if (params.data?.name === "Total") {
            return {
              ...baseStyle,
              backgroundColor: "#e8eaed",
              color: "#182737",
              borderTop: "3px solid #182737",
            };
          }
          return baseStyle;
        },
      },
      {
        field: "loan_amount_gbp",
        headerName: "Amount",
        width: 120,
        cellStyle: (params: any) => {
          const baseStyle = { textAlign: "right" as const };
          if (params.data?.name === "Total") {
            return {
              ...baseStyle,
              backgroundColor: "#e8eaed",
              color: "#182737",
              borderTop: "3px solid #182737",
            };
          }
          return baseStyle;
        },
        valueFormatter: (params: any) => formatCurrency(params.value || 0),
      },
      {
        field: "average_deal_size_gbp",
        headerName: "Average Deal Size",
        width: 150,
        cellStyle: (params: any) => {
          const baseStyle = { textAlign: "right" as const };
          if (params.data?.name === "Total") {
            return {
              ...baseStyle,
              backgroundColor: "#e8eaed",
              color: "#182737",
              borderTop: "3px solid #182737",
            };
          }
          return baseStyle;
        },
        valueFormatter: (params: any) => formatCurrency(params.value || 0),
      },
      {
        field: "percentOfTotal",
        headerName: "% of Total",
        width: 250,
        cellRenderer: chartCellRenderer,
        cellStyle: (params: any) => {
          if (params.data?.name === "Total") {
            return {
              backgroundColor: "#e8eaed",
              color: "#182737",
              borderTop: "3px solid #182737",
            };
          }
          return undefined;
        },
      },
    ];
  }, [chartCellRenderer, groupBy]);

  const chartItems = useMemo(() => {
    if (!displaySummary?.summaries) return [];

    const sortedSummaries = [...displaySummary.summaries];
    if (
      groupBy === "sector" ||
      groupBy === "country" ||
      groupBy === "kill_reason"
    ) {
      sortedSummaries.sort(
        (a: any, b: any) => b.loan_amount_gbp - a.loan_amount_gbp,
      );
    }

    const totalAmount = displaySummary.total?.loan_amount_gbp ?? 0;

    return sortedSummaries.map((item: any) => ({
      name: item.name,
      value: item.loan_amount_gbp,
      pct:
        totalAmount > 0 ? (item.loan_amount_gbp / totalAmount) * 100 : 0,
      dealCount: item.deal_count,
    }));
  }, [displaySummary, groupBy]);

  const calculatedTableHeight = useMemo(() => {
    const headerHeight = 30;
    const rowHeight = 30;
    const rowCount = summaryRowData.length;
    return headerHeight + rowHeight * rowCount;
  }, [summaryRowData]);

  const panelHeight = Math.max(
    calculatedTableHeight + PANEL_HEADER_HEIGHT,
    SUMMARY_PANEL_HEIGHT,
  );

  const groupTitle = getGroupTitle(groupBy);
  const totalDeals = displaySummary?.total?.deal_count ?? 0;
  const totalValue = displaySummary?.total?.loan_amount_gbp ?? 0;

  return (
    <>
      {summary && summary.summaries && (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            gap: 2,
            alignItems: "stretch",
            minHeight: panelHeight,
            mb: 3,
            width: "100%",
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: { xs: 0, lg: 360 },
              display: "flex",
              minHeight: panelHeight,
            }}
          >
            <PipelineSummaryDonutChart
              title={groupTitle}
              items={chartItems}
              totalValue={totalValue}
              totalDeals={totalDeals}
              formatValue={formatCurrency}
              minHeight={panelHeight}
            />
          </Box>

          <Box
            sx={{
              flex: 1.25,
              minWidth: { xs: 0, lg: 520 },
              display: "flex",
              minHeight: panelHeight,
            }}
          >
            <PipelineSummaryTable
              title={`${groupTitle} breakdown`}
              rowData={summaryRowData}
              columnDefs={summaryColumnDefs}
              minHeight={panelHeight}
              dealCount={totalDeals}
            />
          </Box>
        </Box>
      )}
      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </>
  );
};

export default PipelineSummaries;
